import type { AuthClient } from "./auth";
import type { ConfigClient } from "./config";
import type { DelegationClient } from "./delegation";
import type { InstructionClient } from "./instruction";
import type { TransactionClient } from "./transaction";
import type { Optional } from "@utils/helpers/types";
import type { AxiosRequestConfig } from "axios";
import type {
  Account,
  Address,
  Chain,
  PublicClient,
  Transport,
  WalletClient,
} from "viem";

import { createPublicClient, createWalletClient } from "viem";

import { createAPIClient } from "@api/api-client";

import { createAuthClient } from "./auth";
import { createConfigClient } from "./config";
import { createDelegationClient } from "./delegation";
import { createInstructionClient } from "./instruction";
import { createTransactionClient } from "./transaction";

interface BaseConfig {
  authorizationToken?: string;
  // CORS configuration options
  axiosConfig?: AxiosRequestConfig;
  baseURL?: string;
  headers?: Record<string, string>;
  withCredentials?: boolean;
}

export interface CreateClientConfigWithWalletClient extends BaseConfig {
  walletClient: WalletClient;
}

export interface CreateClientConfigWithTransport extends BaseConfig {
  account: Account | Address;
  chain: Chain;
  transport: Transport;
}

export type CreateClientConfig =
  | CreateClientConfigWithWalletClient
  | CreateClientConfigWithTransport;

export interface OtimClientWithWalletClient {
  auth: AuthClient;
  delegation: DelegationClient;
  config: ConfigClient;
  instruction: InstructionClient;
  transaction: TransactionClient;
}

export interface OtimClientWithTransport {
  auth: AuthClient;
  delegation: DelegationClient;
  config: ConfigClient;
  instruction: InstructionClient;
  transaction: TransactionClient;
  //
  publicClient: PublicClient;
}

export type OtimClient = OtimClientWithWalletClient | OtimClientWithTransport;

export function createOtimClient(
  config: CreateClientConfigWithWalletClient,
): OtimClientWithWalletClient;
export function createOtimClient(
  config: CreateClientConfigWithTransport,
): OtimClientWithTransport;
export function createOtimClient(config: CreateClientConfig): OtimClient {
  const { authorizationToken, baseURL, withCredentials, headers, axiosConfig } =
    config;

  const apiClient = createAPIClient({
    ...(authorizationToken && { authorizationToken }),
    ...(baseURL && { baseURL }),
    ...(withCredentials !== undefined && { withCredentials }),
    ...(headers && { headers }),
    ...(axiosConfig && { axiosConfig }),
  });

  let walletClient: WalletClient;
  let publicClient: Optional<PublicClient> = undefined;

  if ("walletClient" in config) {
    walletClient = config.walletClient;
  } else {
    walletClient = createWalletClient({
      chain: config.chain,
      transport: config.transport,
      account: config.account,
    });

    publicClient = createPublicClient({
      chain: config.chain,
      transport: config.transport,
    });
  }

  const auth = createAuthClient(apiClient, walletClient);
  const delegation = createDelegationClient(apiClient, walletClient);
  const configClient = createConfigClient(apiClient);
  const instruction = createInstructionClient(apiClient, walletClient);
  const transaction = createTransactionClient(apiClient, walletClient);

  return {
    auth,
    delegation,
    config: configClient,
    instruction,
    transaction,
    publicClient,
  };
}
